import { useTranslation } from 'react-i18next'
import { formatPercent } from '../../../lib/number'
import { BUCKET_TONE, STATUS_TONE_COLOR_VAR } from '../../actions/types'
import type { StatusDistribution, StatusSlice } from '../types'

interface Props {
  /** Altı alt kırılım (`ActionDashboard.statusDistribution`); `total` yüzdelerin paydasıdır. */
  breakdown: StatusDistribution
  /**
   * Ana durumlar (`ActionDashboard.statusGroups`) — yalnız **öbek adlarını** çözmek için. Adlar
   * donut'un legend'ıyla aynı kaynaktan gelir; istemci ikinci bir eşleme tutmaz (068'in kuralı,
   * 077 §K8'de yeniden onaylandı).
   */
  groups: StatusDistribution
  /**
   * Seçili **kovalar** — `?bucket=` anahtarlarının kova karşılıkları (çağıran `expandSelection` ile
   * açar). Ana durum seçiliyken listede onun <b>iki</b> kovası da bulunur, yani o grubun iki sütunu
   * birden vurgulanır: seçim hangi grafikten yapılmış olursa olsun diğerinde de görünür olmalı.
   */
  selectedBuckets?: readonly string[]
  /**
   * Sütun tıklaması — kovayı alt tablonun süzgecine **ekler ya da çıkarır** (2026-09-16: çoklu seçim).
   * Kararı çağıran verir; grafik yalnız hangi sütuna basıldığını söyler. Verilmezse grafik statiktir.
   */
  onSelect?: (bucket: string) => void
}


/**
 * **"Durumlara Göre Detaylı Dağılım"**: donut'un üç ana durumunun her birini **zamanında / gecikmiş**
 * olarak ikiye ayıran sütun grafiği.
 *
 * <p><b>086 düzeni (mockup 2026-08-13):</b> ana durum adı öbeğin <b>üstünde</b> ve kendi renginde yazar;
 * altındaki iki sütun yalnız gecikme ayrımını ("Zamanında" / "Gecikmiş") söyler. İki ad da
 * <b>sunucudan</b> gelir (`statusName` tek başına okunabilen tam ad, `shortName` bağlam içi kısa ad) →
 * istemci "COMPLETED_LATE → Tamamlandı (Gecikmiş)" gibi bir sözlük tutmaz.
 *
 * <p>Her sütunun üstünde <b>adet ve oran</b> birlikte yazar (mockup: "53 (%28,8)"); oran, filtre sonucu
 * oluşan <b>toplam</b> aksiyon sayısı üzerinden hesaplanır — grup içi değil. Payda ekranda da yazılıdır
 * (alt not), çünkü "%28,8 neyin yüzdesi" sorusunun cevabı grafikten okunamaz.
 *
 * <p><b>Renk:</b> aynı ana durumun iki kırılımı aynı hue'nun iki tonudur ({@code BUCKET_TONE} →
 * {@code STATUS_TONE_COLOR_VAR}) → gruplar renkle, kırılımlar tonla ayrışır. Renk tek taşıyıcı değildir:
 * her sütunun altında kendi adı, üstünde kendi sayısı yazar.
 *
 * <p><b>Çoklu seçim (kullanıcı isteği 2026-09-16):</b> her sütun kendi başına açılıp kapanan bir
 * anahtardır — tıklamak kovayı süzgece ekler, yeniden tıklamak çıkarır; modifier tuş gerekmez. Bu yüzden
 * sütunlar `aria-pressed` taşıyan gerçek `<button>`'lardır (radyo değil): birbirini dışlamayan, aynı anda
 * birden çok açık olabilen seçimin doğru rolü budur.
 *
 * <p><b>Adedi 0 olan kırılım da çizilir</b> (sunucu onu listede bırakır): üç grup × iki sütunluk düzen
 * sabit kalmazsa sütunlar kayar ve kullanıcı hangi kırılıma baktığını şaşırır.
 */
export default function StatusBreakdownChart({
  breakdown,
  groups,
  selectedBuckets = [],
  onSelect,
}: Readonly<Props>) {
  const { t, i18n } = useTranslation()

  if (breakdown.total === 0 || breakdown.slices.length === 0) {
    return null // boş durum kartın kendisinde zaten gösteriliyor
  }

  // Ölçek en yüksek sütuna göre; grup içi normalize edilseydi 53 ile 12 aynı boyda görünürdü.
  const max = breakdown.slices.reduce((acc, s) => Math.max(acc, s.count), 0)
  const columns = groupSlices(breakdown.slices, groups)

  return (
    <div className="home-brk">
      <h3 className="home-brk-title">{t('home.breakdown.title')}</h3>
      <div className="home-brk-plot">
        {columns.map((group) => (
          <div key={group.key} className="home-brk-group">
            <span
              className="home-brk-group-name"
              style={{ color: STATUS_TONE_COLOR_VAR[BUCKET_TONE[group.key] ?? 'neutral'] }}
            >
              {group.name}
            </span>
            <div className="home-brk-cols">
              {group.slices.map((slice) => (
                <BreakdownColumn
                  key={slice.bucket}
                  slice={slice}
                  max={max}
                  lang={i18n.language}
                  // Ana durum seçimi kovalarına AÇILMIŞ hâlde gelir → burada tek bir üyelik sorusu kalır;
                  // sütun, seçim ister kendisinden ister ata grubundan gelsin aynı şekilde vurgulanır.
                  selected={selectedBuckets.includes(slice.bucket)}
                  onSelect={onSelect}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="home-brk-note">
        <i className="pi pi-info-circle" aria-hidden />{' '}
        {t('home.breakdown.note', { total: breakdown.total })}
      </p>
    </div>
  )
}

/**
 * Tek sütun: üstte kısa ad, sonra "adet (%oran)", altta çubuk. `onSelect` verildiğinde tümü gerçek bir
 * `<button>`'dır — klavye ve ekran okuyucu yolu budur; `aria-pressed` seçili durumu renkten bağımsız söyler.
 *
 * <p>Erişilebilir ad, kısa etiket ("Gecikmiş") değil <b>tam</b> addır ("Tamamlandı (Gecikmiş)"): ekran
 * okuyucu kullanıcısı sütunun üstündeki grup başlığını görsel olarak eşleyemez.
 */
function BreakdownColumn({
  slice,
  max,
  lang,
  selected,
  onSelect,
}: {
  slice: StatusSlice
  max: number
  lang: string
  selected: boolean
  onSelect?: (bucket: string) => void
}) {
  const fullName = slice.statusName ?? slice.bucket
  const label = slice.shortName ?? fullName
  const color = STATUS_TONE_COLOR_VAR[BUCKET_TONE[slice.bucket] ?? 'neutral']
  // Adedi 0 olan kırılım da yer tutar; çubuğu görünmez olur ama sayısı ve adı okunur.
  // Çubuk, alanın `count / max` PAYINI kaplar (piksel değil): alan kartın boyuyla esneyince (Yönetici
  // Raporu'nda kart, yanındaki uzun kartın boyuna uzar — 120) çubuklar da orantılı büyür.
  const share = max > 0 ? slice.count / max : 0

  const body = (
    <>
      <span className="home-brk-label" title={fullName}>
        {label}
      </span>
      <span className="home-brk-value" style={{ color }}>
        {slice.count}
        <span className="home-brk-pct">(%{formatPercent(slice.percent, lang)})</span>
      </span>
      <span className="home-brk-track">
        {/* Boşluk + çubuk payları toplamı 1: esnek yükseklik bu oranla bölüşülür. */}
        <span className="home-brk-gap" style={{ flexGrow: 1 - share }} />
        <span className="home-brk-bar" style={{ flexGrow: share, background: color }} />
      </span>
    </>
  )

  const className = `home-brk-col${selected ? ' home-brk-col--on' : ''}`
  return onSelect ? (
    <button
      type="button"
      className={`${className} home-brk-btn`}
      aria-pressed={selected}
      aria-label={fullName}
      onClick={() => onSelect(slice.bucket)}
    >
      {body}
    </button>
  ) : (
    <span className={className}>{body}</span>
  )
}

/**
 * Dilimleri **ana duruma** göre öbekler. Öbek sırası kırılımların sunucudan gelen sırasından, öbek adı ise
 * ana durum dilimlerinden okunur — istemci ikinci bir sabit eşleme tutmaz.
 *
 * <p>Ana durumu olmayan kova (kalkmış İptal) kendi başına bir öbek olur ve adı kendi adıdır; Yönetici
 * Raporu'nda gelmez ama gelirse grafikten düşmez.
 */
function groupSlices(
  slices: StatusSlice[],
  groups: StatusDistribution,
): Array<{ key: string; name: string; slices: StatusSlice[] }> {
  const names = new Map(groups.slices.map((g) => [g.bucket, g.statusName ?? g.bucket]))
  const result: Array<{ key: string; name: string; slices: StatusSlice[] }> = []
  for (const slice of slices) {
    const key = slice.group ?? slice.bucket
    const existing = result.find((g) => g.key === key)
    if (existing) {
      existing.slices.push(slice)
    } else {
      result.push({
        key,
        name: names.get(key) ?? slice.statusName ?? slice.bucket,
        slices: [slice],
      })
    }
  }
  return result
}
